import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { icons } from './sidebar';
import { logout } from '../services/auth';
import { isAdmin } from '../utils/helper';
import { isUserAllowed } from '../utils/roleHelpers';
import useCompanyFeatures from '../hooks/useCompanyFeatures';

const MenuIcon = ({ k }) => (
    <svg className="mm-ic" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{icons[k]}</svg>
);

const closeIcon = <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>;
const userIcon = <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>;
const powerIcon = <><path d="M18.36 6.64a9 9 0 1 1-12.73 0" /><line x1="12" y1="2" x2="12" y2="12" /></>;

/* Mirrors the sidebar's structure exactly so the menu reflects the same
   role-gated destinations. Reports keeps its sub-routes as a nested group. */
const BASE_REPORTS = [
    { key: 'reports.jobsHistory',     to: '/reports/jobs-history' },
    { key: 'reports.jobsPerformance', to: '/reports/jobs-performance' },
    { key: 'reports.assignees',       to: '/reports/jobs-assignees' },
    { key: 'reports.services',        to: '/reports/services-reports' },
    { key: 'reports.expenses',        to: '/reports/expenses' },
];

/**
 * Full-screen "More" menu for mobile — surfaces every page the user can reach,
 * grouped the same way as the desktop sidebar, plus Profile and Sign out
 * (which are otherwise hidden on mobile). Slides up over the bottom nav.
 */
const MoreMenu = ({ open, onClose }) => {
    const { t } = useTranslation();

    const { materialsEnabled } = useCompanyFeatures()

    // Lock body scroll while the sheet is open.
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener('keydown', onKey);
        };
    }, [open, onClose]);

    const handleLogout = () => {
        onClose();
        logout();
        window.location.reload();
    };

    const REPORTS = [...BASE_REPORTS, ...(materialsEnabled ? [{ key: 'reports.materials', to: '/reports/materials' }] : [])]

    const sections = [
        {
            title: t('nav.main'),
            items: [
                { label: t('nav.overview'), to: '/home',    icon: 'overview', allow: isUserAllowed('/home') },
                { label: t('nav.jobs'),     to: '/jobs',    icon: 'jobs',     allow: isUserAllowed('/jobs') },
                { label: t('nav.sales'),    to: '/sales',   icon: 'sales',    allow: isUserAllowed('/sales') },
                { label: t('nav.clients'),  to: '/clients', icon: 'clients',  allow: isUserAllowed('/clients') },
            ],
        },
        {
            title: t('nav.finance'),
            items: [
                { label: t('nav.expenses'),  to: '/expenses',  icon: 'expenses', allow: isUserAllowed('/expenses') },
                { label: t('nav.payroll'),   to: '/payrolls',  icon: 'payroll',  allow: isUserAllowed('/payrolls') },
                { label: t('nav.cashFlow'), to: '/cash-flow', icon: 'cashflow', allow: isUserAllowed('/cash-flow') },
            ],
        },
        {
            title: t('nav.workspace'),
            items: [
                { label: t('nav.calendar'), to: '/calendar', icon: 'calendar', allow: isUserAllowed('/calendar') },
                { label: t('nav.routePlan'), to: '/route-plan', icon: 'route', allow: isUserAllowed('/route-plan') },
                { label: t('nav.services'), to: '/services', icon: 'services', allow: isUserAllowed('/services') },
                ...(materialsEnabled ? [{ label: t('nav.materials'), to: '/materials', icon: 'materials', allow: isUserAllowed('/materials') }] : []),
            ],
            reports: isUserAllowed('/reports/job-reports'),
        },
    ];

    return (
        <div className={`mm-root d-md-none ${open ? 'open' : ''}`} aria-hidden={!open}>
            <div className="mm-backdrop" onClick={onClose} />
            <div className="mm-sheet" role="dialog" aria-modal="true" aria-label={t('nav.menu')}>
                <div className="mm-head">
                    <span className="mm-title">{t('nav.menu')}</span>
                    <button type="button" className="mm-close" onClick={onClose} aria-label={t('nav.closeMenu')}>
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{closeIcon}</svg>
                    </button>
                </div>

                <div className="mm-body">
                    {sections.map((sec) => {
                        const visible = sec.items.filter(i => i.allow);
                        const hasReports = sec.reports;
                        if (visible.length === 0 && !hasReports) return null;
                        return (
                            <div className="mm-section" key={sec.title}>
                                <div className="mm-section-h">{sec.title}</div>
                                {visible.map(i => (
                                    <NavLink key={i.to} to={i.to} className="mm-item" onClick={onClose}>
                                        <MenuIcon k={i.icon} />
                                        <span>{i.label}</span>
                                    </NavLink>
                                ))}
                                {hasReports && (
                                    <div className="mm-reports">
                                        <div className="mm-item mm-item-static">
                                            <MenuIcon k="reports" />
                                            <span>{t('nav.reports')}</span>
                                        </div>
                                        <div className="mm-sublist">
                                            {REPORTS.map(r => (
                                                <NavLink key={r.to} to={r.to} className="mm-subitem" onClick={onClose}>
                                                    <span className="mm-dot" aria-hidden="true" />
                                                    <span>{t(r.key)}</span>
                                                </NavLink>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    <div className="mm-section">
                        <div className="mm-section-h">{t('profile.account')}</div>
                        <NavLink to="/profile" className="mm-item" onClick={onClose}>
                            <svg className="mm-ic" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{userIcon}</svg>
                            <span>{t('profile.profile')}</span>
                        </NavLink>
                        {isAdmin() && (
                            <NavLink to="/settings" className="mm-item" onClick={onClose}>
                                <MenuIcon k="settings" />
                                <span>{t('nav.settings')}</span>
                            </NavLink>
                        )}
                        <button type="button" className="mm-item mm-logout" onClick={handleLogout}>
                            <svg className="mm-ic" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{powerIcon}</svg>
                            <span>{t('nav.signOut')}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MoreMenu;
